/**
 * Entrar con la huella, desde el navegador.
 *
 * ---------------------------------------------------------------------------
 *  Lo que se le promete al usuario, y lo que no
 * ---------------------------------------------------------------------------
 *
 * Se le promete que **no tendrá que escribir la contraseña** en este dispositivo. Y se
 * le dice, con las mismas palabras, que **la contraseña sigue existiendo y sigue
 * sirviendo**: si pierde el teléfono, entra con ella y borra la credencial.
 *
 * Eso no es letra pequeña, es la condición para que esto sea una comodidad y no una
 * trampa. Una forma de entrar que pueda dejar a alguien fuera de su propia cuenta es
 * peor que no tenerla.
 *
 * ---------------------------------------------------------------------------
 *  Dónde está la seguridad, de verdad
 * ---------------------------------------------------------------------------
 *
 * Lo que se guarda en el servidor es la clave **pública**. Aunque alguien se llevara la
 * tabla entera, no podría entrar en ninguna cuenta: le faltaría la clave privada, que
 * vive en el dispositivo y **no sale de él, ni siquiera hacia nosotros**.
 *
 * Aquí, en el navegador, lo único que se hace es pedirle al dispositivo que firme. La
 * comprobación de la firma la hace el servidor.
 */

import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

import type { UserPublic } from '@eldescuadre/contracts';

import {
  getWebAuthnLoginOptions,
  getWebAuthnRegisterOptions,
  verifyWebAuthnLogin,
  verifyWebAuthnRegistration,
} from './endpoints';

/**
 * ¿Puede este dispositivo usar la huella?
 *
 * Se comprueba antes de ofrecer el botón, para no poner una opción que al pulsarla dé
 * un error. `window.PublicKeyCredential` no existe en navegadores viejos ni en
 * contextos sin HTTPS.
 */
export function biometricsSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  return typeof window.PublicKeyCredential !== 'undefined';
}

/**
 * ¿Tiene el dispositivo una huella o una cara configurada?
 *
 * Es distinto de que el navegador sepa hacerlo: un computador sin lector de huellas
 * soporta WebAuthn y no puede usarlo. Preguntarlo evita ofrecer un botón que no lleva a
 * ninguna parte.
 */
export async function hasPlatformAuthenticator(): Promise<boolean> {
  if (!biometricsSupported()) return false;

  try {
    const available = (
      window.PublicKeyCredential as unknown as {
        isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
      }
    ).isUserVerifyingPlatformAuthenticatorAvailable;

    return available ? await available.call(window.PublicKeyCredential) : false;
  } catch {
    return false;
  }
}

/**
 * Vincula la huella de este dispositivo a la cuenta.
 *
 * @param label Cómo llamará el usuario a este dispositivo, para reconocerlo después.
 */
export async function enrollBiometrics(label: string): Promise<{ label: string }> {
  const { options } = await getWebAuthnRegisterOptions(label);

  // El dispositivo pide la huella y devuelve la clave pública firmada.
  const response = await startRegistration({ optionsJSON: options as never });

  return verifyWebAuthnRegistration(response, label);
}

/**
 * Entra con la huella y devuelve la sesión.
 *
 * El resultado es **idéntico** al de escribir la contraseña: mismo token, misma cookie,
 * mismo registro de auditoría. A partir de aquí la aplicación no sabe ni le importa cómo
 * se entró.
 */
export async function loginWithBiometrics(
  email?: string,
): Promise<{ accessToken: string; expiresIn: number; user: UserPublic }> {
  const { options } = await getWebAuthnLoginOptions(email);
  const response = await startAuthentication({ optionsJSON: options as never });

  return verifyWebAuthnLogin(response);
}

/**
 * Traduce el error del navegador a algo que se pueda leer.
 *
 * Cuando el usuario cancela, el navegador lanza un error con un nombre largo y en
 * inglés. Enseñarlo tal cual no ayuda a nadie; y tratarlo como un fallo grave hace
 * pensar que algo se rompió cuando lo único que pasó es que cambió de idea.
 */
export function describeBiometricError(error: unknown): string {
  const name = (error as { name?: string } | null)?.name ?? '';

  if (name === 'NotAllowedError' || name === 'AbortError') {
    return 'Se canceló o no se reconoció la huella. Puedes intentarlo otra vez o entrar con tu contraseña.';
  }
  if (name === 'NotSupportedError') {
    return 'Este dispositivo no puede usar la huella. Entra con tu contraseña.';
  }
  if (name === 'InvalidStateError') {
    return 'Ese dispositivo ya está vinculado a tu cuenta.';
  }
  if (name === 'SecurityError') {
    return 'El navegador no permite usar la huella en esta dirección. Entra con tu contraseña.';
  }

  return 'No se pudo usar la huella. Entra con tu contraseña.';
}
